import { PropertiesPanelControl, PropertiesPanelLabel, PropertiesPanelProperty } from './propertiesPanelProperty';
import React, { ButtonHTMLAttributes } from 'react';
import styled from 'styled-components';
import { BasicButton } from '../buttons';
import { ColorPicker, ColorPickerProps } from '../colorPicker';
import { useDropdown } from '../hooks/useDropdown';
import { FormControl } from '../formControl';
import { isNearWhiteOrBlack } from '../helpers/helpers';

type ColorPickerButtonProps = {
    $color: string | null;
    $block?: boolean;
    $computedColor?: number;
} & React.DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;
const ColorPickerButton = styled(BasicButton)<ColorPickerButtonProps>`
    background-color: ${(props) => props.$color};

    &:after {
        content: '${(props) => props.$color}';
        color: ${(props) => (props.$computedColor ? '#000' : '#fff')};
        mix-blend-mode: ${(props) => (props.$computedColor === -1 ? 'soft-light' : 'hard-light')};
    }

    &:hover:not([disabled]) {
        opacity: 0.8;
        background-color: ${(props) => props.$color} !important;
        color: ${(props) => props.$color} !important;
        &:after {
            color: ${(props) => (props.$computedColor ? '#000' : '#fff')} !important;
        }
    }

    ${(props) =>
        props.$block &&
        `
		border-radius: 4px;
		width: 100%;
		border: 1px #ddd solid;
	`}
`;

export type PropertiesPanelColorPickerButtonProps = {
    color: string;
    colorPickerProps?: Omit<ColorPickerProps, 'color' | 'onChange'>;
    onChange: ColorPickerProps['onChange'];
    disabled?: boolean;
    block?: boolean;
};

export const PropertiesPanelColorPickerButton: React.FC<PropertiesPanelColorPickerButtonProps> = ({ color, colorPickerProps, onChange, disabled, block }) => {
    const [setOpen, , isOpen, Dropdown] = useDropdown();
    return (
        <FormControl>
            <ColorPickerButton
                $block={block}
                $color={color}
                $computedColor={isNearWhiteOrBlack(color ?? '#ffffff')}
                size='medium'
                onClick={(e) => setOpen(e.currentTarget, 'right', 'top')}
                disabled={disabled}
            />
            {isOpen && (
                <Dropdown>
                    <ColorPicker
                        {...colorPickerProps}
                        color={color}
                        onChange={(newColor) => {
                            onChange(newColor);
                        }}
                    />
                </Dropdown>
            )}
        </FormControl>
    );
};

export type PropertiesPanelColorPropertyProps = {
    id: string;
    label: React.ReactNode;
    value: string;
    onChange?: (newValue: string) => void;
    disabled?: boolean;
    block?: boolean;
};
export const PropertiesPanelColorProperty: React.FC<PropertiesPanelColorPropertyProps> = ({ id, label, value, onChange, disabled, block }) => {
    return (
        <PropertiesPanelProperty>
            <PropertiesPanelLabel htmlFor={`${id}-input`}>{label}</PropertiesPanelLabel>
            <PropertiesPanelControl>
                <PropertiesPanelColorPickerButton color={value} onChange={(color) => onChange?.(color)} disabled={disabled} block={block} />
            </PropertiesPanelControl>
        </PropertiesPanelProperty>
    );
};
